/**
 * Layby plan arithmetic — the one definition of "how many payments, and how big".
 *
 * ⚠️  THIS FILE IS A COPY. The canonical version, with its test suite, is
 *     Layaway.info/shared/layby-plan.ts. Liquid cannot call a function and the two
 *     storefronts and two order backends are separate repos, so the same file is
 *     checked into four places:
 *
 *       LAYAWAYDEPOTNZ/assets/layby-plan.js       (storefront quote, NZ)
 *       LADAU/assets/layby-plan.js                (storefront quote, AU)
 *       LAYAWAYDEPOTNZ-vercel/lib/layby-plan.js   (order backend, NZ)
 *       LADAU-VERCEL/lib/layby-plan.js            (order backend, AU)
 *
 *     Change the canonical file first, run `npx tsx shared/layby-plan.test.ts`,
 *     then propagate. A divergence here is a checkout that quotes one term and a
 *     contract that runs another — which is exactly the failure this file exists
 *     to prevent.
 *
 * WHY IT EXISTS. Until 2026-09 every NZ/AU catalogue price was a multiple of the
 * single $25 weekly rate, so `total / 25` came out whole and nobody had to think
 * about the tail. Letting a customer pick $15, $25 or $50 (and pay fortnightly at
 * double) breaks that: only 39% of live totals divide evenly by $15 or $50, and
 * even at $25 it was never universal — 38% of Active contracts already end on a
 * short final payment.
 *
 * WHAT MAKES A QUOTE ACCURATE. The ledger is the authority and already does the
 * right thing: {nz,au}/api/payments/ensure-schedule.ts writes each row as
 * `Math.min(instalment, remainingToSchedule)`, so the last row is whatever is left
 * and nothing over-collects. An accurate checkout shows exactly the rows that loop
 * will write — ceil() payments, the last one short.
 *
 * INTEGER CENTS, ALWAYS. Two traps, both invisible on a $25 grid: Liquid's
 * `divided_by` truncates on two integers, turning a following `| ceil` into a
 * no-op and rounding terms DOWN; and float dollars drift, so 44.10 / 14.70 is
 * 3.0000000000000004 and ceil() invents a payment. Cents are exact under both.
 * Nothing here takes a dollar amount.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.LaybyPlan = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /** Weekly rates a customer may choose, in cents. Fortnightly debits double these. */
  var ALLOWED_WEEKLY_CENTS = [1500, 2500, 5000];

  /** The rate a plan falls back to when nothing valid was selected. */
  var DEFAULT_WEEKLY_CENTS = 2500;

  /**
   * There is deliberately NO maximum term. Every published rate is offered on
   * every cart, and a long plan is disclosed rather than withheld — the card
   * shows the real week count and finish date, which is the honest way to let a
   * customer judge $15/week on a big cart for themselves.
   */

  /**
   * Payment count and tail for one cart at one rate.
   *
   * `weeks` is deliberately computed on the WEEKLY rate even for a fortnightly
   * plan, because that is what goes on the wire and the CRM halves it back:
   * nz/api/webhook/shopify.ts reads `total_duration_weeks` and takes
   * `ceil(durationWeeks / 2)` as the payment count.
   */
  function laybyPlan(totalCents, weeklyRateCents, frequency) {
    if (!isFinite(weeklyRateCents) || weeklyRateCents <= 0) {
      throw new Error('laybyPlan: weeklyRateCents must be positive, got ' + weeklyRateCents);
    }
    var freq = frequency === 'Fortnightly' ? 'Fortnightly' : 'Weekly';
    var rate = Math.round(weeklyRateCents);
    var total = Math.max(0, Math.round(totalCents || 0));
    var instalmentCents = freq === 'Fortnightly' ? rate * 2 : rate;

    // A $0 cart has no plan. Returning zeros rather than throwing keeps the
    // selector renderable on an empty cart, which is a real checkout state.
    if (total === 0) {
      return {
        weeklyRateCents: rate, instalmentCents: instalmentCents, frequency: freq,
        weeks: 0, count: 0, fullCount: 0, finalCents: 0, isEven: true
      };
    }

    var weeks = Math.ceil(total / rate);
    var count = freq === 'Fortnightly' ? Math.ceil(weeks / 2) : weeks;
    var finalCents = total - (count - 1) * instalmentCents;

    return {
      weeklyRateCents: rate,
      instalmentCents: instalmentCents,
      frequency: freq,
      weeks: weeks,
      count: count,
      fullCount: count - 1,
      finalCents: finalCents,
      isEven: finalCents === instalmentCents
    };
  }

  /**
   * The date the last debit falls on: the first payday plus one interval per
   * remaining payment. Date-only, UTC, no ambient clock.
   */
  function finishDate(firstPayDate, plan) {
    if (!firstPayDate || plan.count <= 0) return null;
    var intervalDays = plan.frequency === 'Fortnightly' ? 14 : 7;
    var d = new Date(Date.UTC(
      firstPayDate.getUTCFullYear(), firstPayDate.getUTCMonth(), firstPayDate.getUTCDate()
    ));
    d.setUTCDate(d.getUTCDate() + (plan.count - 1) * intervalDays);
    return d;
  }

  /**
   * `1500` -> `"$15"`, `1250` -> `"$12.50"`, `124900` -> `"$1,249"`. Whole dollars
   * lose the `.00`; thousands are grouped, because a cart total is read alongside
   * Shopify's own money filters and an ungrouped `$1249` next to `$1,249` looks
   * like a different number.
   */
  function centsToDisplay(cents, currencySymbol) {
    var sym = currencySymbol === undefined ? '$' : currencySymbol;
    var v = Math.round(cents) / 100;
    var whole = Math.floor(Math.abs(v));
    var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var fraction = v === Math.floor(v) ? '' : '.' + Math.abs(v).toFixed(2).slice(-2);
    return (v < 0 ? '-' : '') + sym + grouped + fraction;
  }

  /**
   * One line a customer can act on: the instalment, the count, and the short tail
   * named rather than hidden. Hiding the tail is what makes a quote wrong.
   */
  function planSentence(plan, currencySymbol) {
    if (plan.count <= 0) return '';
    var unit = plan.frequency === 'Fortnightly' ? 'fortnightly payments' : 'weekly payments';
    var instalment = centsToDisplay(plan.instalmentCents, currencySymbol);

    // A cart smaller than one instalment is one short payment, not "0 payments of
    // $25 then a final $7" — which is arithmetically true and useless to read.
    if (plan.count === 1) {
      return 'a single payment of ' + centsToDisplay(plan.finalCents, currencySymbol);
    }
    if (plan.isEven) return plan.count + ' ' + unit + ' of ' + instalment;
    return plan.fullCount + ' ' + unit + ' of ' + instalment +
      ', then a final ' + centsToDisplay(plan.finalCents, currencySymbol);
  }

  return {
    ALLOWED_WEEKLY_CENTS: ALLOWED_WEEKLY_CENTS,
    DEFAULT_WEEKLY_CENTS: DEFAULT_WEEKLY_CENTS,
    laybyPlan: laybyPlan,
    finishDate: finishDate,
    centsToDisplay: centsToDisplay,
    planSentence: planSentence
  };
}));
